import { useEffect, useState } from "react";
import { PALETTE, FONT_MONO } from "@/components/cobrain/tokens";

/* Ambient depth for the /services hero: a soft amber wash + slow drifting
   grain, plus a rotating one-line proof ticker. Reduced-motion safe. */

export function HeroAmbient() {
  return (
    <div aria-hidden style={{ position: "absolute", inset: 0, overflow: "hidden", pointerEvents: "none", zIndex: 0 }}>
      <div className="svc-amb svc-amb-a" />
      <div className="svc-amb svc-amb-b" />
      <div className="svc-grain" />
      <style>{`
        .svc-amb { position: absolute; border-radius: 50%; filter: blur(70px); opacity: 0.5; }
        .svc-amb-a { width: 46vw; height: 46vw; max-width: 620px; max-height: 620px; top: -16%; right: -8%;
          background: radial-gradient(circle, rgba(196,130,58,0.30), rgba(196,130,58,0) 68%);
          animation: svcDriftA 26s ease-in-out infinite alternate; }
        .svc-amb-b { width: 38vw; height: 38vw; max-width: 520px; max-height: 520px; bottom: -22%; left: -10%;
          background: radial-gradient(circle, rgba(232,180,88,0.22), rgba(232,180,88,0) 70%);
          animation: svcDriftB 32s ease-in-out infinite alternate; }
        .svc-grain { position: absolute; inset: -50%; opacity: 0.035;
          background-image: radial-gradient(rgba(0,0,0,0.9) 1px, transparent 1px);
          background-size: 3px 3px; animation: svcGrain 40s linear infinite; }
        @keyframes svcDriftA { to { transform: translate3d(-40px, 30px, 0) scale(1.08); } }
        @keyframes svcDriftB { to { transform: translate3d(46px, -26px, 0) scale(1.06); } }
        @keyframes svcGrain { to { transform: translate3d(60px, 40px, 0); } }
        @media (prefers-reduced-motion: reduce) {
          .svc-amb, .svc-grain { animation: none !important; }
        }
      `}</style>
    </div>
  );
}

const TICKS = [
  "Missed-call callback — live inside a week",
  "Quote follow-up — about 6 hrs a week back",
  "Recall outreach — running unattended since week one",
  "Inbox triage — routed before anyone opens it",
];

export function HeroTicker() {
  const [i, setI] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    if (paused) return;
    const t = window.setInterval(() => setI((n) => (n + 1) % TICKS.length), 3400);
    return () => window.clearInterval(t);
  }, [paused]);

  return (
    <div
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      style={{
        marginTop: 18, display: "inline-flex", alignItems: "center", gap: 10,
        minHeight: 24, maxWidth: "100%",
      }}
    >
      <span aria-hidden style={{
        width: 7, height: 7, borderRadius: "50%", background: PALETTE.amber, flexShrink: 0,
        boxShadow: `0 0 0 4px rgba(196,130,58,0.18)`,
      }} />
      <span
        key={i}
        aria-live="off"
        className="svc-tick"
        style={{
          fontFamily: FONT_MONO, fontSize: 11.5, letterSpacing: "0.06em",
          color: PALETTE.muted, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
        }}
      >
        {TICKS[i]}
      </span>
      <style>{`
        .svc-tick { animation: svcTickIn 420ms ease-out both; }
        @keyframes svcTickIn { from { opacity: 0; transform: translateY(5px); } to { opacity: 1; transform: none; } }
        @media (prefers-reduced-motion: reduce) { .svc-tick { animation: none; } }
      `}</style>
    </div>
  );
}

export function AvailabilityPill() {
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 7,
      padding: "6px 12px", borderRadius: 999,
      border: `1px solid ${PALETTE.line}`, background: "rgba(196,130,58,0.08)",
      fontFamily: FONT_MONO, fontSize: 10.5, letterSpacing: "0.1em",
      textTransform: "uppercase", color: PALETTE.muted, fontWeight: 700,
      whiteSpace: "nowrap",
    }}>
      <span aria-hidden style={{ width: 6, height: 6, borderRadius: "50%", background: PALETTE.amber }} />
      Limited audit slots each month
    </span>
  );
}
